/**
 * PendingMaterialsCard — Iter229h
 *
 * Small Appraisal-Overview card that surfaces ⏳ pending order items —
 * Finish-role barges + on-site-measure flashings that can't be ordered
 * until the roof is fitted and final widths/lengths are taken on-site.
 *
 * Acts as a checklist gate: the office sees "N items still need
 * measurement" before they release the final supplier PO. Reads from
 * GET /api/appraisals/{id}/materials-order-list (totals.pending_*).
 */
import React, { useEffect, useState } from 'react';
import { ClipboardList, Clock } from 'lucide-react';

export default function PendingMaterialsCard({ api, appraisalId }) {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!api || !appraisalId) return undefined;
    setLoading(true);
    api.get(`/appraisals/${appraisalId}/materials-order-list`)
      .then((r) => { if (!cancelled) setTotals(r.data?.totals || null); })
      .catch(() => { if (!cancelled) setTotals(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, appraisalId]);

  if (loading) return null;
  if (!totals) return null;
  const pendingTotal = (totals.pending_total_pieces || 0);
  if (pendingTotal === 0) {
    // Don't render an empty/green card — keep the Overview tight. Only
    // surface when there's actually something to flag.
    return null;
  }
  return (
    <div
      className="bg-amber-50 ring-1 ring-amber-300 rounded-lg p-3 flex items-start gap-3"
      data-testid="overview-pending-materials-card"
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center">
        <Clock size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-amber-900">Pending materials — measure after install</h3>
          <span className="text-[11px] font-semibold text-amber-800 font-mono-num">
            {pendingTotal} piece{pendingTotal === 1 ? '' : 's'} · {(totals.pending_total_stock_m || 0).toFixed(2)} m
          </span>
        </div>
        <p className="text-xs text-amber-800 mt-0.5">
          {totals.pending_barge_pieces > 0 && (
            <>
              <strong>{totals.pending_barge_pieces}</strong> finish-role barge{totals.pending_barge_pieces === 1 ? '' : 's'}
              {totals.pending_flashing_pieces > 0 && ' · '}
            </>
          )}
          {totals.pending_flashing_pieces > 0 && (
            <>
              <strong>{totals.pending_flashing_pieces}</strong> on-site-measure flashing{totals.pending_flashing_pieces === 1 ? '' : 's'}
            </>
          )}
          {' — '}order can't be released until on-site measurements are confirmed.
        </p>
        <a
          href="#components"
          onClick={(e) => {
            e.preventDefault();
            const evt = new CustomEvent('rtr:navigate-tab', { detail: { tab: 'Components' } });
            window.dispatchEvent(evt);
          }}
          className="inline-flex items-center gap-1 text-[11px] text-amber-900 font-semibold mt-1 hover:underline"
          data-testid="overview-pending-materials-link"
        >
          <ClipboardList size={11} /> Open Materials Order List
        </a>
      </div>
    </div>
  );
}
